/**
 * Batch Out through Aster Gateway.
 *
 * POST /api/merchant/batch-out starts it, GET /api/merchant/batch-out/{id}
 * follows it, GET /api/merchant/batch-outs is the history. Every terminal's
 * verdict on screen is Aster Gateway's own answer; a batch is shown as settled
 * only when the gateway said so.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2, XCircle, Clock, AlertTriangle, Layers, RefreshCw, Loader2, ChevronDown, ChevronRight, CreditCard,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/config/api';

const POLL_MS = 3000;
const POLL_LIMIT_MS = 6 * 60 * 1000;

const OUTCOME = {
  closed: { label: 'Closed', className: 'bg-green-100 text-green-700', Icon: CheckCircle2 },
  nothing_to_close: { label: 'Nothing to close', className: 'bg-green-50 text-green-700', Icon: CheckCircle2 },
  refused: { label: 'Not closed', className: 'bg-red-100 text-red-700', Icon: XCircle },
  unreachable: { label: 'Not reached', className: 'bg-red-100 text-red-700', Icon: XCircle },
  unsupported: { label: 'Close on terminal', className: 'bg-amber-100 text-amber-700', Icon: AlertTriangle },
  unknown: { label: 'No answer', className: 'bg-slate-100 text-slate-600', Icon: AlertTriangle },
};

const money = (cents) => (cents == null ? '—' : `$${(cents / 100).toFixed(2)}`);
const when = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

async function call(path, headers, options = {}) {
  const res = await fetch(`${API}${path}`, { headers, ...options });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const detail = typeof body?.detail === 'string' ? body.detail : 'Aster Gateway is unavailable right now';
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return body;
}

function Verdict({ batch }) {
  if (batch.status === 'running') {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <Loader2 className="w-4 h-4 animate-spin" /> Waiting for each card terminal to answer…
      </div>
    );
  }
  if (batch.all_settled === true) {
    return <div className="flex items-center gap-2 text-sm font-medium text-green-700"><CheckCircle2 className="w-4 h-4" /> Every card terminal is batched out</div>;
  }
  if (batch.all_settled === false) {
    return <div className="flex items-center gap-2 text-sm font-medium text-amber-700"><AlertTriangle className="w-4 h-4" /> Not every card terminal batched out — see each terminal below</div>;
  }
  return (
    <div className="flex items-center gap-2 text-sm font-medium text-red-600">
      <XCircle className="w-4 h-4" /> {batch.error || 'No answer from Aster Gateway'}
    </div>
  );
}

function TerminalRows({ terminals }) {
  if (!terminals?.length) return null;
  return (
    <div className="divide-y border rounded-lg mt-3" data-testid="batch-terminals">
      {terminals.map((t, i) => {
        const o = OUTCOME[t.outcome] || OUTCOME.unknown;
        return (
          <div key={`${t.terminal_id || t.terminal}-${i}`} className="p-3 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 dark:text-white flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-slate-400" /> {t.terminal}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {t.terminal_id ? <>Terminal ID {t.terminal_id} · </> : null}{t.message}
                {t.code ? <span className="text-slate-400"> (code {t.code})</span> : null}
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              {t.batch_number && <span className="text-slate-500">Batch #{t.batch_number}</span>}
              {t.transaction_count != null && <span className="text-slate-500">{t.transaction_count} sales</span>}
              {t.total_cents != null && <span className="font-medium text-slate-800 dark:text-slate-200">{money(t.total_cents)}</span>}
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${o.className}`}>
                <o.Icon className="w-3 h-3" /> {o.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ batch }) {
  if (batch.status === 'running') return <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Running</span>;
  if (batch.all_settled === true) return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Batched out</span>;
  if (batch.all_settled === false) return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Partly batched out</span>;
  if (batch.status === 'not_found') return <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Answer lost</span>;
  return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Not started</span>;
}

export default function BatchOutPanel({ token, restaurantId }) {
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(restaurantId ? { 'X-Merchant-ID': String(restaurantId) } : {}),
  }), [token, restaurantId]);

  const [history, setHistory] = useState({ batch_outs: [], total: 0, connected: true });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [current, setCurrent] = useState(null);
  const [starting, setStarting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const pollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setHistory(await call('/merchant/batch-outs', headers));
      setLoadError(null);
    } catch (e) {
      setLoadError(e.message);
    }
    setLoading(false);
  }, [headers, token]);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => () => clearTimeout(pollRef.current), []);

  const follow = useCallback((batch, startedAt = Date.now()) => {
    setCurrent(batch);
    if (batch.is_final) { loadHistory(); return; }
    if (Date.now() - startedAt > POLL_LIMIT_MS) {
      toast.warning('Aster Gateway is still working on the batch-out. Check back in a few minutes.');
      loadHistory();
      return;
    }
    pollRef.current = setTimeout(async () => {
      try {
        follow(await call(`/merchant/batch-out/${batch.id}`, headers), startedAt);
      } catch (e) {
        toast.error(e.message);
      }
    }, POLL_MS);
  }, [headers, loadHistory]);

  // A batch-out still running in the history is followed on load.
  useEffect(() => {
    const running = history.batch_outs.find(b => b.status === 'running');
    if (running && !current) follow(running);
  }, [history, current, follow]);

  const start = async () => {
    setConfirmOpen(false);
    setStarting(true);
    try {
      follow(await call('/merchant/batch-out', headers, { method: 'POST' }));
    } catch (e) {
      toast.error(e.message);
      loadHistory();
    }
    setStarting(false);
  };

  const running = current && current.status === 'running';

  return (
    <div className="space-y-4" data-testid="batch-out-panel">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Layers className="w-4 h-4 text-slate-400" /> Batch Out
              </h3>
              <p className="text-sm text-slate-500 mt-1 max-w-xl">
                Closes the open batch on every card terminal through Aster Gateway, so the day's card sales settle.
                Each terminal answers for itself.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadHistory} disabled={loading} data-testid="refresh-batches-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              <Button
                className="bg-teal-600 hover:bg-teal-700 text-white"
                onClick={() => setConfirmOpen(true)}
                disabled={!history.connected || starting || running}
                data-testid="batch-out-btn"
              >
                {starting || running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Layers className="w-4 h-4 mr-2" />}
                Batch out now
              </Button>
            </div>
          </div>
          {!history.connected && (
            <p className="text-sm text-slate-500 mt-4" data-testid="batch-not-connected">
              Card payments are not set up for this restaurant yet. Contact chain29 support to connect Aster Gateway.
            </p>
          )}
          {current && (
            <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-700" data-testid="batch-current">
              <p className="text-xs text-slate-400 mb-2">Requested {when(current.requested_at)}</p>
              <Verdict batch={current} />
              {current.note && <p className="text-xs text-slate-500 mt-1">{current.note}</p>}
              <TerminalRows terminals={current.terminals} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b bg-slate-50 dark:bg-slate-800/50 text-xs font-medium text-slate-500 uppercase tracking-wider">
            Batch history
          </div>
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : loadError ? (
            <p className="p-6 text-sm text-red-600" data-testid="batch-load-error">{loadError}</p>
          ) : history.batch_outs.length === 0 ? (
            <div className="py-12 text-center" data-testid="no-batches">
              <Clock className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No batch-outs yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {history.batch_outs.map(b => (
                <div key={b.id}>
                  <button
                    type="button"
                    onClick={() => setExpanded(expanded === b.id ? null : b.id)}
                    className="w-full px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <span className="flex items-center gap-2 text-sm">
                      {expanded === b.id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      {when(b.requested_at)}
                    </span>
                    <span className="flex items-center gap-4 text-xs text-slate-500">
                      {b.terminal_count != null && <span>{b.terminal_count} terminal(s)</span>}
                      {b.closed_count != null && <span>{b.closed_count} closed</span>}
                      {b.nothing_to_close_count ? <span>{b.nothing_to_close_count} already empty</span> : null}
                      {b.failed_count ? <span className="text-red-600">{b.failed_count} not closed</span> : null}
                      <StatusBadge batch={b} />
                    </span>
                  </button>
                  {expanded === b.id && (
                    <div className="px-4 pb-4">
                      {b.error && <p className="text-xs text-red-600">{b.error}</p>}
                      <TerminalRows terminals={b.terminals} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Batch out every card terminal?</DialogTitle>
            <DialogDescription>
              Aster Gateway will close the open batch on each card terminal. Finish any card payment in progress first.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={start} data-testid="confirm-batch-out-btn">Batch out</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
